/**
 * GW Featured Collection
 *
 * Uses Splide.js with Grid extension for:
 * - Mobile: 2x2 grid layout per page
 * - Desktop: Horizontal carousel with 4 visible cards
 *
 * Uses MutationObserver to detect when tab panels become visible,
 * then initializes/reinitializes the slider.
 *
 * Supports Shopify Product Recommendations API for individual blocks
 * via data-recommendations attribute on carousel elements.
 */

class GwFeaturedCollection extends HTMLElement {
  constructor() {
    super();
    this.sliders = new Map();
    this.observer = null;
    this.pendingInit = new Map(); // Debounce map for carousel initialization
    this.desktopQuery = window.matchMedia('(min-width: 750px)');
    this.onBreakpointChange = this.onBreakpointChange.bind(this);
  }

  connectedCallback() {
    this.desktopQuery.addEventListener('change', this.onBreakpointChange);
    this.initVisibleSliders();
    this.observeTabPanels();
    this.loadRecommendations();
  }

  disconnectedCallback() {
    this.desktopQuery.removeEventListener('change', this.onBreakpointChange);
    this.destroySliders();
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  /**
   * A carousel whose layout setting for the current breakpoint is "grid" is
   * laid out as a static CSS grid, so Splide must not mount over it.
   */
  isStaticGrid(carousel) {
    const layout = this.desktopQuery.matches
      ? carousel.dataset.desktopLayout
      : carousel.dataset.mobileLayout;

    return layout === 'grid';
  }

  onBreakpointChange() {
    const carousels = this.querySelectorAll('.gw-featured-collection__carousel');

    carousels.forEach((carousel) => {
      if (this.isStaticGrid(carousel)) {
        const slider = this.sliders.get(carousel);
        if (slider) {
          slider.destroy();
          this.sliders.delete(carousel);
        }
        return;
      }

      // Already mounted sliders handle their own breakpoints internally
      if (this.sliders.has(carousel)) return;

      const tabpanel = carousel.closest('[role="tabpanel"]');
      const isVisible = !tabpanel || !tabpanel.hasAttribute('hidden');
      if (isVisible) this.initSingleSlider(carousel);
    });
  }

  initVisibleSliders() {
    if (typeof Splide === 'undefined') {
      document.addEventListener('DOMContentLoaded', () => this.initVisibleSliders());
      return;
    }

    // Only initialize carousels that are visible (not in hidden tab panels)
    const carousels = this.querySelectorAll('.gw-featured-collection__carousel');
    carousels.forEach((carousel) => {
      const tabpanel = carousel.closest('[role="tabpanel"]');
      if (!tabpanel || !tabpanel.hasAttribute('hidden')) {
        // Skip empty recommendation carousels — loadRecommendations() will init after fetch
        if (carousel.hasAttribute('data-recommendations')) {
          const slideList = carousel.querySelector('.splide__list');
          if (!slideList || slideList.children.length === 0) return;
        }
        this.initSingleSlider(carousel);
      }
    });
  }

  initSingleSlider(carousel) {
    // Destroy existing slider first if present
    if (this.sliders.has(carousel)) {
      this.sliders.get(carousel).destroy();
      this.sliders.delete(carousel);
    }

    if (this.isStaticGrid(carousel)) return;

    const splide = new Splide(carousel, {
      type: 'slide',
      autoWidth: true,
      padding: { left: 'var(--gw-page-width-offset)', right: 'var(--gw-page-width-offset)' },
      pagination: true,
      arrows: false,
      perPage: 4,
      perMove: 4,
      // perMove: 1,
      gap: '8px',
      grid: false,
      // focus: 0,
      // omitEnd: true,
      classes: {
        pagination: 'splide__pagination gw-featured-collection__dots',
        page: 'splide__pagination__page gw-featured-collection__dot',
      },
      breakpoints: {
        750: {
          autoWidth: false,
          perPage: 1,
          perMove: 1,
          gap: '16px',
          focus: undefined,
          omitEnd: false,
          grid: {
            rows: 2,
            cols: Number(carousel.dataset.columnsMobile) || 2,
            gap: {
              row: '8px',
              col: '8px',
            },
          },
        },
      },
    });

    if (window.splide?.Extensions?.Grid) {
      splide.mount({ Grid: window.splide.Extensions.Grid });
    } else {
      splide.mount();
    }

    this.sliders.set(carousel, splide);
    this.setupWheelNavigation(carousel, splide);
  }

  /**
   * The "view all" button lives in the shared tabs header, so it has to follow
   * whichever tab is active rather than staying on the first block's collection.
   */
  updateCollectionLink(carousel) {
    const content = carousel.closest('.gw-featured-collection__content');
    const shopBtn = this.querySelector('[data-collection-link]');
    if (!content || !shopBtn) return;

    const { collectionUrl, collectionTitle } = content.dataset;
    if (!collectionUrl) return;

    shopBtn.href = collectionUrl;

    const label = shopBtn.dataset.collectionLabel;
    if (label && collectionTitle) {
      shopBtn.setAttribute('aria-label', `${label} - ${collectionTitle}`);
    }
  }

  setupWheelNavigation(carousel, splide) {
    const track = carousel.querySelector('.splide__track');
    const list = carousel.querySelector('.splide__list');
    if (!track || !list) return;

    let snapTimeout = null;

    track.addEventListener(
      'wheel',
      (e) => {
        // Only respond to horizontal scroll (trackpad horizontal swipe)
        if (Math.abs(e.deltaX) < Math.abs(e.deltaY)) return;
        if (Math.abs(e.deltaX) < 2) return;

        const { Move, Controller } = splide.Components;
        const position = Move.getPosition();
        const endPosition = Move.toPosition(Controller.getEnd());

        // Allow natural page scroll at carousel boundaries
        if (e.deltaX > 0 && position <= endPosition) return;
        if (e.deltaX < 0 && position >= 0) return;

        e.preventDefault();

        // Disable CSS transitions for direct 1:1 tracking with the finger
        list.style.transition = 'none';

        // Move the track directly by the scroll delta, clamped to boundaries
        const newPosition = Math.max(
          endPosition,
          Math.min(0, position - e.deltaX)
        );
        Move.translate(newPosition);

        // Snap to nearest slide when scrolling stops
        if (snapTimeout) clearTimeout(snapTimeout);
        snapTimeout = setTimeout(() => {
          // Re-enable transitions so the snap animates smoothly
          list.style.transition = '';

          const currentPos = Move.getPosition();
          const endIdx = Controller.getEnd();
          let closestIndex = 0;
          let closestDist = Infinity;

          for (let i = 0; i <= endIdx; i++) {
            const dist = Math.abs(currentPos - Move.toPosition(i));
            if (dist < closestDist) {
              closestDist = dist;
              closestIndex = i;
            }
          }

          splide.go(closestIndex);
        }, 150);
      },
      { passive: false }
    );
  }

  observeTabPanels() {
    const panels = this.querySelectorAll('[role="tabpanel"]');
    if (panels.length === 0) return;

    this.observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'hidden') {
          const panel = mutation.target;
          const carousel = panel.querySelector('.gw-featured-collection__carousel');

          if (!carousel) return;

          // Cancel any pending initialization for this carousel
          if (this.pendingInit.has(carousel)) {
            cancelAnimationFrame(this.pendingInit.get(carousel));
            this.pendingInit.delete(carousel);
          }

          // Only initialize if panel is now visible
          if (!panel.hasAttribute('hidden')) {
            // Point the shared "view all" button at the newly selected tab's collection
            this.updateCollectionLink(carousel);

            // Skip empty recommendation carousels
            if (carousel.hasAttribute('data-recommendations')) {
              const slideList = carousel.querySelector('.splide__list');
              if (!slideList || slideList.children.length === 0) return;
            }

            const frameId = requestAnimationFrame(() => {
              this.pendingInit.delete(carousel);
              this.initSingleSlider(carousel);
            });
            this.pendingInit.set(carousel, frameId);
          }
        }
      });
    });

    panels.forEach((panel) => {
      this.observer.observe(panel, { attributes: true, attributeFilter: ['hidden'] });
    });
  }

  loadRecommendations() {
    const baseUrl = this.dataset.recommendationsUrl;
    const sectionId = this.dataset.sectionId;
    const productId = this.dataset.productId;

    if (!baseUrl || !sectionId || !productId) return;

    const recCarousels = this.querySelectorAll('[data-recommendations="true"]');
    if (recCarousels.length === 0) return;

    recCarousels.forEach((carousel) => {
      const intent = carousel.dataset.recommendationIntent;
      const limit = carousel.dataset.recommendationLimit || 8;
      const carouselId = carousel.id;

      const url = `${baseUrl}?product_id=${productId}&section_id=${sectionId}&limit=${limit}&intent=${intent}`;

      fetch(url)
        .then((response) => response.text())
        .then((text) => {
          const html = document.createElement('div');
          html.innerHTML = text;

          // Find the matching carousel in the response by its unique ID
          const responseCarousel = html.querySelector(`#${CSS.escape(carouselId)}`);

          if (!responseCarousel) {
            this.handleEmptyRecommendations(carousel);
            return;
          }

          const responseList = responseCarousel.querySelector('.splide__list');
          if (!responseList || responseList.children.length === 0) {
            this.handleEmptyRecommendations(carousel);
            return;
          }

          // Replace the empty slide list with the populated one
          const currentList = carousel.querySelector('.splide__list');
          if (currentList) {
            currentList.innerHTML = responseList.innerHTML;
          }

          // Update products count
          carousel.dataset.productsCount = responseList.children.length;

          // Initialize Splide if the carousel's tab panel is currently visible
          const tabpanel = carousel.closest('[role="tabpanel"]');
          if (!tabpanel || !tabpanel.hasAttribute('hidden')) {
            this.initSingleSlider(carousel);
          }
          // If hidden, MutationObserver will handle init when tab is clicked
        })
        .catch((error) => {
          console.error('Failed to load recommendations:', error);
          this.handleEmptyRecommendations(carousel);
        });
    });
  }

  handleEmptyRecommendations(carousel) {
    // Hide the tab and panel when no recommendations are available
    const tabpanel = carousel.closest('[role="tabpanel"]');
    if (tabpanel) {
      const tabId = tabpanel.getAttribute('aria-labelledby');
      const tab = this.querySelector(`#${CSS.escape(tabId)}`);
      if (tab) tab.style.display = 'none';
      tabpanel.style.display = 'none';

      // If only one tab remains visible, hide the tablist
      const visibleTabs = this.querySelectorAll('[role="tab"]:not([style*="display: none"])');
      if (visibleTabs.length <= 1) {
        const tablist = this.querySelector('[role="tablist"]');
        if (tablist) tablist.style.display = 'none';
      }
    }
  }

  destroySliders() {
    this.sliders.forEach((slider) => {
      if (slider && slider.destroy) {
        slider.destroy();
      }
    });
    this.sliders.clear();
  }
}

customElements.define('gw-featured-collection', GwFeaturedCollection);

// Re-initialize for Shopify section rendering in the theme editor
document.addEventListener('shopify:section:load', (event) => {
  const section = event.target;
  const component = section.querySelector('gw-featured-collection');
  if (component) {
    component.initVisibleSliders();
    component.loadRecommendations();
  }
});